"use client"

import { dateLib, roomLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { activePlanMasters } from "@agentcall/core/master"
import { planMasterType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { addDays, addMonths, max, parseISO, startOfDay, startOfMonth } from "date-fns"
import { useEffect, useState } from "react"
import { Check, Repeat } from "react-feather"
import { useForm } from "react-hook-form"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { toast } from "sonner"
import * as z from "zod"
import { planApi, planSchemas } from "@/api/client/plan"
import { roomApi } from "@/api/client/room"
import { getErrorMessage } from "@/api/instance"
import { PaymentMethodRequiredDialog } from "@/components/payment/PaymentMethodRequiredDialog"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { RoomHeader } from "@/components/properties/RoomHeader"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import { Checkbox } from "@/components/ui/checkbox/checkbox"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form"
import { Loading } from "@/components/ui/loading"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group/radio-group"
import { usePaymentMethodRequirement } from "@/hooks/usePaymentMethodRequirement"
import { cn } from "@/lib/utils"
import { useIsEchoesOwner } from "@/store/session"

// Form schema
const planSubscribeFormSchema = z.object({
  planMasterId: planMasterType.idZ,
  startDate: z.date({
    error: "プラン利用開始日を選択してください",
  }),
  agreement: z.boolean().refine((val) => val === true, {
    message: "承諾が必要です",
  }),
})

type PlanSubscribeFormValues = z.infer<typeof planSubscribeFormSchema>

export function PlanAddPage() {
  const { propertyId: propertyIdParam, roomId: roomIdParam } = useParams()
  const propertyId = propertyIdParam ? Number(propertyIdParam) : undefined
  const roomId = roomIdParam ? Number(roomIdParam) : undefined
  const [searchParams] = useSearchParams()
  const selectedTenantId = Number(searchParams.get("tenantId"))
  const navigate = useNavigate()
  // Echoes 連携オーナーはエコーズプランのみ、それ以外は従来プランのみ選択可(設計 §8)。
  const isEchoesOwner = useIsEchoesOwner()
  const selectablePlanMasters = activePlanMasters.filter(
    (planMaster) => planMasterType.isEchoesPlanMasterId(planMaster.id) === isEchoesOwner
  )
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    ensurePaymentMethod,
    isCheckingPaymentMethod,
    isPaymentMethodDialogOpen,
    setIsPaymentMethodDialogOpen,
  } = usePaymentMethodRequirement()

  const hasValidIds = propertyId !== undefined && roomId !== undefined

  // Fetch room data with tenants
  const {
    data: roomResponse,
    isLoading: isRoomLoading,
    error: roomError,
  } = roomApi.useRoomGet(
    roomId,
    { withProperty: "true", withTenants: "true", withPlans: "true" },
    {
      query: {
        enabled: hasValidIds,
      },
    }
  )

  const planCreateMutation = planApi.usePlanCreate({
    mutation: {
      onSuccess: () => {
        toast.success("プランへの加入が完了しました")
        queryClient.invalidateQueries({ queryKey: ["/properties"] })
        if (propertyId !== undefined && roomId !== undefined) {
          navigate(`/properties/${propertyId}/rooms/${roomId}`, { state: { shouldRefetch: true } })
        } else {
          navigate("/properties")
        }
      },
      onError: (error) => {
        console.error("Error creating plan:", error)
        toast.error("プラン加入に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const form = useForm<PlanSubscribeFormValues>({
    resolver: zodResolver(planSubscribeFormSchema),
    defaultValues: {
      planMasterId: isEchoesOwner
        ? planMasterType.PLAN_MASTER_ID_ECHOES_FULL
        : planMasterType.PLAN_MASTER_ID_STANDARD,
      startDate: new Date(),
      agreement: false,
    },
  })

  // 賃貸借契約開始日、今日、加入中のプラン終了日の翌日のうち最も遅い日から選択できる
  const selectedTenant =
    roomResponse?.room?.tenants?.find((tenant) => tenant.id === selectedTenantId) ??
    roomLib.roomCurrentTenant(roomResponse?.room)
  const activePlanEndDate = roomLib.roomActivePlan(roomResponse?.room, selectedTenant)?.endDate
  const tenantStartDate = selectedTenant?.startDate
  const minStartDate = max([
    startOfDay(new Date()),
    ...(tenantStartDate ? [startOfDay(parseISO(tenantStartDate))] : []),
    ...(activePlanEndDate ? [addDays(parseISO(activePlanEndDate), 1)] : []),
  ])
  const minStartDateISO = dateLib.formatDateISO(minStartDate)

  // 部屋の取得後に加入中のプランの終了日が分かるので、初期値を下限に合わせる
  useEffect(() => {
    form.setValue("startDate", parseISO(minStartDateISO))
  }, [form, minStartDateISO])

  const onSubmit = async (data: PlanSubscribeFormValues) => {
    const planStartDate = data.startDate as Date

    if (!hasValidIds) {
      toast.error("物件情報または部屋情報が見つかりません。")
      return
    }

    if (!roomResponse?.room) {
      toast.error("部屋情報が取得できません。")
      return
    }

    if (!selectedTenant) {
      toast.error("有効なテナント情報が見つかりません。")
      return
    }

    if (dateLib.formatDateISO(planStartDate) < selectedTenant.startDate) {
      form.setError("startDate", {
        message: "プラン利用開始日は賃貸借契約開始日以降を選択してください",
      })
      return
    }

    if (!(await ensurePaymentMethod())) {
      return
    }

    setIsSubmitting(true)
    try {
      const requestData = planSchemas.planCreateBody.parse({
        planMasterId: data.planMasterId,
        roomId: roomId,
        startDate: dateLib.formatDateISO(planStartDate),
      })

      await planCreateMutation.mutateAsync({ data: requestData })
    } catch (error) {
      // 表示は onError が担当する
      console.error("Error submitting plan:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  // Calculate subscription start date (first day of next month from service start date)
  const startDate = form.watch("startDate")
  const subscriptionStartDate = startDate
    ? dateLib.formatDateJapanese(startOfMonth(addMonths(startDate, 1)))
    : ""

  // Loading state
  if (isRoomLoading) {
    return <Loading className="mt-10" text="部屋情報を読み込んでいます..." />
  }

  // Error state
  if (!hasValidIds || roomError || !roomResponse) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-slate-600">
          {hasValidIds ? "部屋情報の取得に失敗しました。" : "部屋情報が見つかりませんでした。"}
        </div>
        <Button onClick={() => navigate(-1)} variant="outline">
          前のページに戻る
        </Button>
      </div>
    )
  }

  const room = roomResponse.room
  const property = room.property
  const currentTenant = selectedTenant

  if (!currentTenant) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-slate-600">有効なテナント情報が見つかりません。</div>
        <Button onClick={() => navigate(-1)} variant="outline">
          前のページに戻る
        </Button>
      </div>
    )
  }

  if (!propertyId || !roomId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDまたは部屋IDが不正です。</div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PropertyHeader propertyId={propertyId} />
      <RoomHeader roomId={roomId} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Contract information */}
          <div className="rounded-md bg-slate-100 p-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-500">物件名</p>
                <p className="text-sm font-semibold text-primary">{property?.name ?? ""}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-500">居室番号</p>
                <p className="text-sm font-semibold text-primary">{room.name}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-500">賃借人氏名</p>
                <p className="text-sm font-semibold text-primary">{currentTenant.name}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-500">電話番号</p>
                <p className="text-sm font-semibold text-primary">
                  {formatPhone(currentTenant.phone1, currentTenant.phone2, currentTenant.phone3)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-500">メールアドレス</p>
                <p className="text-sm font-semibold text-primary whitespace-normal break-words">
                  {currentTenant.email ?? ""}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-500">初回契約開始日</p>
                <p className="text-sm font-semibold text-primary">
                  {currentTenant.startDate
                    ? dateLib.formatDateJapanese(currentTenant.startDate)
                    : ""}
                </p>
              </div>
            </div>
          </div>

          {/* Plan selection */}
          <Card>
            <CardContent className="p-4">
              <div className="mb-4 flex items-center gap-2">
                <Repeat className="h-4 w-4 text-slate-600" />
                <h3 className="text-base font-semibold text-slate-600">プランを選択</h3>
              </div>

              <FormField
                control={form.control}
                name="planMasterId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        onValueChange={(value) => {
                          if (value === planMasterType.PLAN_MASTER_ID_PLUS) {
                            return
                          }
                          field.onChange(value)
                        }}
                        value={field.value}
                        className="space-y-3"
                      >
                        {selectablePlanMasters.map((planMaster) => {
                          const isPlusPlan = planMaster.id === planMasterType.PLAN_MASTER_ID_PLUS
                          const isSelected = field.value === planMaster.id

                          return (
                            <div key={planMaster.id} className="relative flex items-center">
                              <RadioGroupItem
                                value={planMaster.id}
                                id={planMaster.id}
                                disabled={isPlusPlan}
                                className="sr-only"
                              />
                              <label
                                htmlFor={isPlusPlan ? undefined : planMaster.id}
                                className={cn("block w-full", {
                                  "cursor-pointer": !isPlusPlan,
                                  "cursor-not-allowed": isPlusPlan,
                                })}
                              >
                                <Card
                                  className={cn("transition-all", {
                                    "border-[#458CF7] bg-[#F0F7FE] ring-2 ring-[#458CF7]":
                                      isSelected,
                                    "hover:shadow-md": !isPlusPlan,
                                    "border-slate-200 bg-slate-50 opacity-60": isPlusPlan,
                                  })}
                                >
                                  <CardContent className="p-4">
                                    <div className="space-y-4">
                                      <div className="flex items-start justify-between">
                                        <h3 className="text-base font-semibold">
                                          {planMaster.name}
                                        </h3>
                                        <p className="text-sm font-semibold">
                                          {planMaster.monthlyPrice.toLocaleString()}円 (税込) / 月額
                                        </p>
                                      </div>
                                      <div className="space-y-2">
                                        {planMaster.features.map((feature: string) => (
                                          <div
                                            key={feature}
                                            className="flex items-center gap-2 font-semibold"
                                          >
                                            <Check className="h-3.5 w-3.5 text-primary" />
                                            <p className="text-xs text-slate-600">{feature}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </label>
                            </div>
                          )
                        })}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Service start date */}
          <Card>
            <CardContent className="p-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>プラン利用開始日</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="日付を選択"
                        fromDate={minStartDate}
                        defaultMonth={
                          tenantStartDate ? startOfDay(parseISO(tenantStartDate)) : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Subscription start date display */}
          <div className="rounded-md bg-slate-100 p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500">ご請求開始日</p>
              <p className="text-sm font-semibold text-primary">{subscriptionStartDate}</p>
            </div>
          </div>

          {/* Agreement checkbox */}
          <Card>
            <CardContent className="p-4">
              <FormField
                control={form.control}
                name="agreement"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal">
                        当フォームの内容で、サービス料金が発生することに承諾します。
                      </FormLabel>
                    </div>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
              戻る
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isCheckingPaymentMethod || !form.formState.isValid}
              loading={isSubmitting || isCheckingPaymentMethod || planCreateMutation.isPending}
            >
              プランに加入する
            </Button>
          </div>
        </form>
      </Form>
      <PaymentMethodRequiredDialog
        open={isPaymentMethodDialogOpen}
        onOpenChange={setIsPaymentMethodDialogOpen}
      />
    </div>
  )
}
